import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Globe, ChevronRight, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { getScalingSignals } from "@/lib/api";

export default function ScalingSignalsPage() {
  const [expandedId, setExpandedId] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await getScalingSignals();
        if (!cancelled) setSignals(data);
      } catch (e) {
        if (!cancelled) setSignals([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="fade-in" data-testid="scaling-signals-page">
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight text-gray-900 mb-1">Scaling Signals</h1>
        <p className="text-sm text-gray-500">Brands showing signs of aggressive scaling based on creative activity.</p>
      </div>

      <div className="space-y-4" data-testid="scaling-signals-list">
        {signals.length === 0 && !loading && (
          <div className="text-center py-16 border border-gray-200 rounded-lg bg-gray-50/50">
            <p className="text-sm text-gray-600 mb-1">No scaling signals yet.</p>
            <p className="text-xs text-gray-400">Connect the backend to start surfacing brands that are scaling.</p>
          </div>
        )}
        {signals.map((signal) => (
          <div key={signal.id} className="bg-white border border-gray-200 rounded-lg hover:shadow-sm transition-shadow" data-testid={`signal-card-${signal.id}`}>
            <div className="p-5">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{signal.brand}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="bg-gray-100 text-gray-500 px-2 py-0.5 text-[10px] font-medium rounded-md">{signal.platform}</span>
                      <div className="flex items-center gap-1">
                        {signal.countries.map((c) => (
                          <span key={c} className="text-[10px] text-gray-400 flex items-center gap-0.5">
                            <Globe className="w-3 h-3" />{c}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedId(expandedId === signal.id ? null : signal.id)}
                  className="text-xs text-primary hover:text-primary/90 font-medium flex items-center gap-1 transition-colors"
                  data-testid={`view-ads-${signal.id}`}
                >
                  View Ads <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expandedId === signal.id ? "rotate-90" : ""}`} />
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">New Creatives</div>
                  <div className="text-lg font-semibold text-gray-900 flex items-center gap-1.5">
                    {signal.newCreatives}
                    <Zap className="w-3.5 h-3.5 text-amber-500" />
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Top Creative Days</div>
                  <div className="text-lg font-semibold text-emerald-600">{signal.topCreativeDays}d</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Countries Active</div>
                  <div className="text-lg font-semibold text-gray-900">{signal.countries.length}</div>
                </div>
              </div>

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                <p className="text-sm text-gray-800 leading-relaxed">{signal.explanation}</p>
              </div>
            </div>

            {expandedId === signal.id && (
              <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50" data-testid={`signal-expanded-${signal.id}`}>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Related Ads</div>
                <div className="space-y-2">
                  {(competitorAds["comp-1"] || []).slice(0, 3).map((ad) => (
                    <div key={ad.id} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-100">
                      <img src={ad.thumbnail} alt="" className="w-10 h-10 rounded-md object-cover border border-gray-100" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 truncate">{ad.copyPreview}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] text-gray-400">{ad.platform}</span>
                          <span className="text-[11px] font-medium text-emerald-600">{ad.daysActive}d active</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
